/**
 * Shared wash-tier catalog screen. Rendered by the owner route
 * (app/owner/subscriptions/wash-tiers.tsx) and the admin owner-twin
 * (app/admin/owner/[id]/subscriptions/wash-tiers.tsx). The only difference is
 * the API client — admins pass makeOwnerApi(ownerId) so every call is scoped to
 * that owner. One component = guaranteed admin parity.
 */
import React, { useState, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator } from 'react-native';
import { useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import Card from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';
import ContentContainer from '../components/ContentContainer';
import { useToast } from '../components/Toast';
import { useTheme } from '../context/ThemeContext';
import { useNetwork } from '../context/NetworkContext';
import type { ColorTheme } from '../utils/theme';
import { spacing, fontSize, fontWeight } from '../utils/theme';
import type { TierApiClient } from './FleetTiersScreen';

export interface WashTier {
  id: string;
  name: string;
  priceInCents: number;
  displayOrder: number;
  isActive: boolean;
  freeVacMinutes?: number | null;
}

const dollars = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const toCents = (s: string) => Math.round(parseFloat(s || '0') * 100);

interface Props {
  client: TierApiClient;
  adminOwnerId?: string;
  /** Owner wash-tier usage only: per-tier free-vac minutes + the grant-window setting. */
  showVacuumFields?: boolean;
}

/** Manage the owner's wash-tier catalog (create/edit/archive/reorder). */
interface Upsell { id: string; name: string; priceCents: number; defaultDelayMs: number; displayOrder: number; isActive: boolean }

export default function WashTiersScreen({ client, adminOwnerId, showVacuumFields = false }: Props) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();
  const { isOffline } = useNetwork();

  const [tiers, setTiers] = useState<WashTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [newName, setNewName] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [newFreeVac, setNewFreeVac] = useState('');

  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [editFreeVac, setEditFreeVac] = useState('');

  // Free-vac redemption window (owner setting, minutes)
  const [grantWindow, setGrantWindow] = useState('');
  const [grantWindowLoaded, setGrantWindowLoaded] = useState(false);
  const [grantWindowBusy, setGrantWindowBusy] = useState(false);
  // ---- Upsells (1.4 P15): owner-level catalog reused across bays, like tiers ----
  const [upsellsList, setUpsellsList] = useState<Upsell[]>([]);
  const [upName, setUpName] = useState('');
  const [upPrice, setUpPrice] = useState('');
  const [upDelay, setUpDelay] = useState('0');
  const [upEditId, setUpEditId] = useState<string | null>(null);
  const [upEditName, setUpEditName] = useState('');
  const [upEditPrice, setUpEditPrice] = useState('');
  const [upEditDelay, setUpEditDelay] = useState('0');
  const [upBusy, setUpBusy] = useState(false);
  const fetchUpsells = useCallback(async () => {
    try { setUpsellsList(await client.get<Upsell[]>('/api/upsells')); } catch { /* catalog optional on old servers */ }
  }, [client]);
  useFocusEffect(useCallback(() => { fetchUpsells(); }, [fetchUpsells]));
  const parseDelaySec = (raw: string): number | undefined => {
    const n = parseFloat(raw.trim() || '0');
    if (isNaN(n) || n < 0 || n > 120) { toast.show({ type: 'error', title: 'Delay must be 0–120 seconds' }); return undefined; }
    return Math.round(n * 1000);
  };
  const addUpsell = async () => {
    const name = upName.trim();
    if (!name) { toast.show({ type: 'error', title: 'Name required' }); return; }
    const cents = toCents(upPrice);
    if (!Number.isFinite(cents) || cents < 0) { toast.show({ type: 'error', title: 'Enter a valid price (0 allowed)' }); return; }
    try {
      setUpBusy(true);
      await client.post('/api/upsells', { name, priceCents: cents });
      setUpName(''); setUpPrice('');
      toast.show({ type: 'success', title: 'Upsell added' });
      await fetchUpsells();
    } catch (err: any) { toast.show({ type: 'error', title: 'Could not add', message: err.message }); }
    finally { setUpBusy(false); }
  };
  const saveUpsell = async (id: string) => {
    const name = upEditName.trim();
    if (!name) { toast.show({ type: 'error', title: 'Name required' }); return; }
    const cents = toCents(upEditPrice);
    if (!Number.isFinite(cents) || cents < 0) { toast.show({ type: 'error', title: 'Enter a valid price' }); return; }
    try {
      setUpBusy(true);
      await client.patch(`/api/upsells/${id}`, { name, priceCents: cents });
      setUpEditId(null);
      toast.show({ type: 'success', title: 'Upsell saved' });
      await fetchUpsells();
    } catch (err: any) { toast.show({ type: 'error', title: 'Could not save', message: err.message }); }
    finally { setUpBusy(false); }
  };
  const toggleUpsell = async (u: Upsell) => {
    try { await client.patch(`/api/upsells/${u.id}`, { isActive: !u.isActive }); await fetchUpsells(); }
    catch (err: any) { toast.show({ type: 'error', title: 'Could not update', message: err.message }); }
  };
  const moveUpsell = async (i: number, dir: -1 | 1) => {
    const act = upsellsList.filter(u => u.isActive);
    const j = i + dir; if (j < 0 || j >= act.length) return;
    const ids = act.map(u => u.id); [ids[i], ids[j]] = [ids[j], ids[i]];
    try { await client.post('/api/upsells/reorder', { ids }); await fetchUpsells(); }
    catch (err: any) { toast.show({ type: 'error', title: 'Could not reorder', message: err.message }); }
  };
  const deleteUpsell = async (u: Upsell) => {
    try { await client.delete(`/api/upsells/${u.id}`); toast.show({ type: 'success', title: 'Upsell deleted' }); await fetchUpsells(); }
    catch (err: any) { toast.show({ type: 'error', title: 'Still assigned', message: err.message }); }
  };

  const fetchTiers = useCallback(async () => {
    try {
      const rows = await client.get<WashTier[]>('/api/wash-tiers');
      setTiers(rows);
    } catch (err: any) {
      if (!isOffline) toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setLoading(false);
    }
    if (showVacuumFields) {
      // Best-effort: the tiers list is the primary content; the window setting
      // card shows a loading placeholder until this resolves.
      try {
        const s = await client.get<{ vacGrantWindowMinutes: number }>('/api/owner/vac-grant-setting');
        setGrantWindow(String(s.vacGrantWindowMinutes));
        setGrantWindowLoaded(true);
      } catch { /* silent */ }
    }
  }, [isOffline, client, showVacuumFields]);

  useFocusEffect(useCallback(() => { fetchTiers(); }, [fetchTiers]));

  const active = tiers.filter(t => t.isActive);
  const archived = tiers.filter(t => !t.isActive);

  // Blank = no free-vac benefit (null); else 0-120 minutes. Returns undefined
  // on validation failure (after toasting).
  const parseFreeVac = (raw: string): number | null | undefined => {
    const s = raw.trim();
    if (!s) return null;
    const n = parseInt(s, 10);
    if (isNaN(n) || n < 0 || n > 120) {
      toast.show({ type: 'error', title: 'Free vac minutes must be 0–120' });
      return undefined;
    }
    return n;
  };

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) { toast.show({ type: 'error', title: 'Name required' }); return; }
    const cents = toCents(newPrice);
    if (!cents || cents <= 0) { toast.show({ type: 'error', title: 'Enter a valid price' }); return; }
    const body: any = { name, priceInCents: cents };
    if (showVacuumFields) {
      const fv = parseFreeVac(newFreeVac);
      if (fv === undefined) return;
      body.freeVacMinutes = fv;
    }
    try {
      setBusy(true);
      await client.post('/api/wash-tiers', body);
      setNewName(''); setNewPrice(''); setNewFreeVac('');
      toast.show({ type: 'success', title: 'Tier added' });
      await fetchTiers();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Could not add', message: err.message });
    } finally { setBusy(false); }
  };

  const startEdit = (t: WashTier) => {
    setEditId(t.id); setEditName(t.name); setEditPrice((t.priceInCents / 100).toFixed(2));
    setEditFreeVac(t.freeVacMinutes != null ? String(t.freeVacMinutes) : '');
  };

  const saveEdit = async (id: string) => {
    const name = editName.trim();
    if (!name) { toast.show({ type: 'error', title: 'Name required' }); return; }
    const cents = toCents(editPrice);
    if (!cents || cents <= 0) { toast.show({ type: 'error', title: 'Enter a valid price' }); return; }
    const body: any = { name, priceInCents: cents };
    if (showVacuumFields) {
      const fv = parseFreeVac(editFreeVac);
      if (fv === undefined) return;
      body.freeVacMinutes = fv;
    }
    try {
      setBusy(true);
      await client.patch(`/api/wash-tiers/${id}`, body);
      setEditId(null);
      toast.show({ type: 'success', title: 'Saved' });
      await fetchTiers();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Could not save', message: err.message });
    } finally { setBusy(false); }
  };

  const saveGrantWindow = async () => {
    const n = parseInt(grantWindow.trim(), 10);
    if (isNaN(n) || n < 15 || n > 10080) {
      toast.show({ type: 'error', title: 'Window must be 15–10080 minutes' });
      return;
    }
    try {
      setGrantWindowBusy(true);
      await client.patch('/api/owner/vac-grant-setting', { vacGrantWindowMinutes: n });
      toast.show({ type: 'success', title: 'Redemption window saved' });
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Could not save', message: err.message });
    } finally { setGrantWindowBusy(false); }
  };

  const archive = async (t: WashTier) => {
    try {
      setBusy(true);
      await client.delete(`/api/wash-tiers/${t.id}`);
      toast.show({ type: 'success', title: 'Archived', message: `"${t.name}" hidden from new selections.` });
      await fetchTiers();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally { setBusy(false); }
  };

  const restore = async (t: WashTier) => {
    try {
      setBusy(true);
      await client.patch(`/api/wash-tiers/${t.id}`, { isActive: true });
      await fetchTiers();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally { setBusy(false); }
  };

  const move = async (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= active.length) return;
    const reordered = [...active];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setTiers([...reordered, ...archived]); // optimistic
    try {
      await client.post('/api/wash-tiers/reorder', { order: reordered.map(t => t.id) });
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Reorder failed', message: err.message });
      fetchTiers();
    }
  };

  if (loading) {
    return <View style={styles.center}><ActivityIndicator color={colors.primary} /></View>;
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <ContentContainer>
        {adminOwnerId && (
          <Text style={styles.adminBanner}>Admin acting on behalf of owner {adminOwnerId}</Text>
        )}
        <Card style={styles.card}>
          <Text style={styles.sectionTitle}>Wash Tiers</Text>
          <Text style={styles.help}>
            Your menu of named wash levels and prices. Tier names must be unique. Optional: Allow free Vac minutes after a wash.
          </Text>

          {active.length === 0 ? (
            <Text style={styles.empty}>No wash tiers yet. Add your first one below.</Text>
          ) : (
            active.map((t, i) => (
              <View key={t.id} style={styles.row}>
                {editId === t.id ? (
                  <View style={{ flex: 1 }}>
                    <View style={styles.editRow}>
                      <View style={{ flex: 2 }}>
                        <Input placeholder="Name" value={editName} onChangeText={setEditName} maxLength={50} />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Input placeholder="$" value={editPrice} onChangeText={(v) => setEditPrice(v.replace(/[^0-9.]/g, ''))} keyboardType="decimal-pad" />
                      </View>
                    </View>
                    {showVacuumFields && (
                      <Input
                        label="Free vac minutes (blank = none)"
                        placeholder="e.g. 15"
                        value={editFreeVac}
                        onChangeText={(v) => setEditFreeVac(v.replace(/[^0-9]/g, ''))}
                        keyboardType="number-pad"
                      />
                    )}
                    <View style={styles.editActions}>
                      <Button title="Cancel" variant="outline" size="sm" onPress={() => setEditId(null)} style={{ flex: 1 }} />
                      <Button title="Save" size="sm" loading={busy} onPress={() => saveEdit(t.id)} style={{ flex: 1 }} />
                    </View>
                  </View>
                ) : (
                  <>
                    <View style={styles.reorder}>
                      <TouchableOpacity onPress={() => move(i, -1)} disabled={i === 0} hitSlop={8}>
                        <Ionicons name="chevron-up" size={18} color={i === 0 ? colors.borderLight : colors.textLight} />
                      </TouchableOpacity>
                      <TouchableOpacity onPress={() => move(i, 1)} disabled={i === active.length - 1} hitSlop={8}>
                        <Ionicons name="chevron-down" size={18} color={i === active.length - 1 ? colors.borderLight : colors.textLight} />
                      </TouchableOpacity>
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.tierName}>{t.name}</Text>
                      <Text style={styles.tierPrice}>{dollars(t.priceInCents)}</Text>
                      {showVacuumFields && t.freeVacMinutes != null && t.freeVacMinutes > 0 && (
                        <View style={styles.vacBadge}>
                          <Ionicons name="sparkles" size={11} color={colors.primary} />
                          <Text style={styles.vacBadgeText}>+{t.freeVacMinutes} min free vac</Text>
                        </View>
                      )}
                    </View>
                    <TouchableOpacity onPress={() => startEdit(t)} style={styles.iconBtn} hitSlop={8}>
                      <Ionicons name="pencil" size={18} color={colors.primary} />
                    </TouchableOpacity>
                    <TouchableOpacity onPress={() => archive(t)} style={styles.iconBtn} hitSlop={8}>
                      <Ionicons name="archive-outline" size={18} color={colors.danger} />
                    </TouchableOpacity>
                  </>
                )}
              </View>
            ))
          )}
        </Card>

        <Card style={styles.card}>
          <Text style={styles.sectionTitle}>Add Wash Tier</Text>
          <View style={styles.editRow}>
            <View style={{ flex: 2 }}>
              <Input label="Name" placeholder="e.g. Premium" value={newName} onChangeText={setNewName} maxLength={50} />
            </View>
            <View style={{ flex: 1 }}>
              <Input label="Price ($)" placeholder="0.00" value={newPrice} onChangeText={(v) => setNewPrice(v.replace(/[^0-9.]/g, ''))} keyboardType="decimal-pad" />
            </View>
          </View>
          {showVacuumFields && (
            <Input
              label="Free vac minutes (blank = none)"
              placeholder="e.g. 15"
              value={newFreeVac}
              onChangeText={(v) => setNewFreeVac(v.replace(/[^0-9]/g, ''))}
              keyboardType="number-pad"
            />
          )}
          <Button
            title="Add Tier"
            variant="outline"
            loading={busy}
            onPress={handleAdd}
            icon={<Ionicons name="add" size={18} color={colors.primary} />}
            style={{ marginTop: spacing.xs }}
          />
        </Card>

        {/* ---- Upsells (1.4 P15) ---- */}
        <Card style={styles.card}>
          <Text style={styles.sectionTitle}>Upsells</Text>
          <Text style={styles.help}>
            Upsells customers can buy with any wash (hot wax, tire shine, ceramic…). Add to individual bays.
          </Text>
          {upsellsList.filter(u => u.isActive).length === 0 ? (
            <Text style={styles.empty}>No upsells yet. Add your first one below.</Text>
          ) : (
            upsellsList.filter(u => u.isActive).map((u, i, arr) => (
              <View key={u.id} style={styles.row}>
                {upEditId === u.id ? (
                  <View style={{ flex: 1 }}>
                    <View style={styles.editRow}>
                      <View style={{ flex: 2 }}><Input placeholder="Name" value={upEditName} onChangeText={setUpEditName} maxLength={48} /></View>
                      <View style={{ flex: 1 }}><Input placeholder="$" value={upEditPrice} onChangeText={(v) => setUpEditPrice(v.replace(/[^0-9.]/g, ''))} keyboardType="decimal-pad" /></View>
                    </View>
                    <View style={styles.editActions}>
                      <Button title="Cancel" variant="outline" size="sm" onPress={() => setUpEditId(null)} style={{ flex: 1 }} />
                      <Button title="Save" size="sm" loading={upBusy} onPress={() => saveUpsell(u.id)} style={{ flex: 1 }} />
                    </View>
                  </View>
                ) : (
                  <>
                    <View style={styles.reorder}>
                      <TouchableOpacity onPress={() => moveUpsell(i, -1)} disabled={i === 0} hitSlop={8}>
                        <Ionicons name="chevron-up" size={18} color={i === 0 ? colors.borderLight : colors.textLight} />
                      </TouchableOpacity>
                      <TouchableOpacity onPress={() => moveUpsell(i, 1)} disabled={i === arr.length - 1} hitSlop={8}>
                        <Ionicons name="chevron-down" size={18} color={i === arr.length - 1 ? colors.borderLight : colors.textLight} />
                      </TouchableOpacity>
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.tierName}>{u.name}</Text>
                      <Text style={styles.tierPrice}>{dollars(u.priceCents)}</Text>
                    </View>
                    <TouchableOpacity onPress={() => { setUpEditId(u.id); setUpEditName(u.name); setUpEditPrice((u.priceCents / 100).toFixed(2)); }} style={styles.iconBtn} hitSlop={8}>
                      <Ionicons name="pencil" size={18} color={colors.primary} />
                    </TouchableOpacity>
                    <TouchableOpacity onPress={() => toggleUpsell(u)} style={styles.iconBtn} hitSlop={8}>
                      <Ionicons name="archive-outline" size={18} color={colors.danger} />
                    </TouchableOpacity>
                  </>
                )}
              </View>
            ))
          )}
        </Card>

        <Card style={styles.card}>
          <Text style={styles.sectionTitle}>Add Upsell</Text>
          <View style={styles.editRow}>
            <View style={{ flex: 2 }}><Input label="Name" placeholder="e.g. Hot Wax" value={upName} onChangeText={setUpName} maxLength={48} /></View>
            <View style={{ flex: 1 }}><Input label="Price ($)" placeholder="0.00" value={upPrice} onChangeText={(v) => setUpPrice(v.replace(/[^0-9.]/g, ''))} keyboardType="decimal-pad" /></View>
          </View>
          <Button title="Add Upsell" loading={upBusy} onPress={addUpsell} />
        </Card>

        {upsellsList.some(u => !u.isActive) && (
          <Card style={styles.card}>
            <Text style={styles.sectionTitle}>Archived Upsells</Text>
            {upsellsList.filter(u => !u.isActive).map(u => (
              <View key={u.id} style={styles.row}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.tierName}>{u.name}</Text>
                  <Text style={styles.tierPrice}>{dollars(u.priceCents)}</Text>
                </View>
                <TouchableOpacity onPress={() => toggleUpsell(u)} style={styles.iconBtn} hitSlop={8}>
                  <Ionicons name="refresh" size={18} color={colors.primary} />
                </TouchableOpacity>
                <TouchableOpacity onPress={() => deleteUpsell(u)} style={styles.iconBtn} hitSlop={8}>
                  <Ionicons name="trash-outline" size={18} color={colors.danger} />
                </TouchableOpacity>
              </View>
            ))}
          </Card>
        )}

        {showVacuumFields && (
          <Card style={styles.card}>
            <Text style={styles.sectionTitle}>Free-Vac Redemption Window</Text>
            <Text style={styles.help}>
              How long after a wash the free vacuum stays redeemable.
            </Text>
            {grantWindowLoaded ? (
              <>
                <Input
                  label="Window (minutes)"
                  value={grantWindow}
                  onChangeText={(v) => setGrantWindow(v.replace(/[^0-9]/g, ''))}
                  keyboardType="number-pad"
                />
                <Button
                  title="Save Window"
                  variant="outline"
                  loading={grantWindowBusy}
                  onPress={saveGrantWindow}
                  icon={<Ionicons name="time-outline" size={18} color={colors.primary} />}
                  style={{ marginTop: spacing.xs }}
                />
              </>
            ) : (
              <Text style={styles.empty}>Loading setting…</Text>
            )}
          </Card>
        )}

        {archived.length > 0 && (
          <Card style={styles.card}>
            <Text style={styles.sectionTitle}>Archived</Text>
            <Text style={styles.help}>Hidden from new selections; existing plans/records keep working.</Text>
            {archived.map(t => (
              <View key={t.id} style={styles.row}>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.tierName, { color: colors.textLight }]}>{t.name}</Text>
                  <Text style={styles.tierPrice}>{dollars(t.priceInCents)}</Text>
                </View>
                <Button title="Restore" variant="outline" size="sm" onPress={() => restore(t)} />
              </View>
            ))}
          </Card>
        )}

        <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: c.background },
  adminBanner: { fontSize: fontSize.xs, color: c.textLight, fontFamily: 'monospace', marginBottom: spacing.sm },
  card: { marginBottom: spacing.md },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.xs },
  help: { fontSize: fontSize.sm, color: c.textSecondary, marginBottom: spacing.md },
  empty: { fontSize: fontSize.sm, color: c.textLight, fontStyle: 'italic', paddingVertical: spacing.sm },
  row: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: c.borderLight },
  reorder: { alignItems: 'center', justifyContent: 'center' },
  tierName: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text },
  tierPrice: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: 2 },
  iconBtn: { padding: spacing.xs },
  editRow: { flexDirection: 'row', gap: spacing.sm, alignItems: 'flex-start' },
  editActions: { flexDirection: 'row', gap: spacing.sm, marginTop: spacing.xs },
  vacBadge: { flexDirection: 'row', alignItems: 'center', gap: 3, marginTop: 2, alignSelf: 'flex-start' },
  vacBadgeText: { fontSize: fontSize.xs, color: c.primary, fontWeight: fontWeight.medium },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
